import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

const emptySmsTemplateForm = () => ({
  name: '',
  type: '',
  template: '',
  description: '',
  category: '',
  maxLength: 160,
  priority: 1,
  sendTime: '',
  frequency: 'immediate',
  isDefault: false,
  language: 'en',
  isActive: true,
});

/** Owns SMS templates (CRUD + the shared Add/Edit form) and SMS send logs
 * (filterable, paginated) for the SMS Management tab. */
export default function useSmsManagement({ activeTab }) {
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [loadingSmsTemplates, setLoadingSmsTemplates] = useState(false);
  const [smsLogs, setSmsLogs] = useState([]);
  const [loadingSmsLogs, setLoadingSmsLogs] = useState(false);
  const [smsStats, setSmsStats] = useState(null);
  const [smsFields, setSmsFields] = useState({});
  const [smsTypes, setSmsTypes] = useState([]);
  const [smsCategories, setSmsCategories] = useState([]);
  const [showAddSmsTemplateModal, setShowAddSmsTemplateModal] = useState(false);
  const [editingSmsTemplate, setEditingSmsTemplate] = useState(null);
  const [selectedSmsFilter, setSelectedSmsFilter] = useState(null); // 'sent', 'failed', 'pending', 'all'
  const [smsLogPagination, setSmsLogPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [selectedSmsLog, setSelectedSmsLog] = useState(null);
  const [showSmsLogDetails, setShowSmsLogDetails] = useState(false);
  const [smsLogFilters, setSmsLogFilters] = useState({ search: '', status: '', startDate: '', endDate: '' });
  const [smsTemplateForm, setSmsTemplateForm] = useState(emptySmsTemplateForm());
  // Set by handlePreviewSmsTemplate but — same as before this refactor — there's
  // no modal wired up to actually show it. Kept for fidelity; worth a follow-up.
  const [smsPreview, setSmsPreview] = useState('');
  const [showSmsPreview, setShowSmsPreview] = useState(false);

  const fetchSmsTemplates = async () => {
    setLoadingSmsTemplates(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setSmsTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
    } finally {
      setLoadingSmsTemplates(false);
    }
  };

  const fetchSmsStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setSmsStats(await response.json());
    } catch (error) {
      console.error('Error fetching SMS stats:', error);
    }
  };

  const fetchSmsFields = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/fields`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setSmsFields(await response.json());
    } catch (error) {
      console.error('Error fetching SMS fields:', error);
    }
  };

  const fetchSmsTypes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/types`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setSmsTypes(data.types || []);
        setSmsCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching SMS types:', error);
    }
  };

  const fetchSmsLogs = async (page = 1, statusFilter = null) => {
    setLoadingSmsLogs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');

      const statusToUse = statusFilter || smsLogFilters.status;
      if (statusToUse && statusToUse !== 'all') {
        params.append('status', statusToUse.toUpperCase());
      }
      if (smsLogFilters.search) params.append('search', smsLogFilters.search);
      if (smsLogFilters.startDate) params.append('startDate', smsLogFilters.startDate);
      if (smsLogFilters.endDate) params.append('endDate', smsLogFilters.endDate);

      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setSmsLogs(data.logs || []);
        setSmsLogPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
    } finally {
      setLoadingSmsLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sms') {
      fetchSmsTemplates();
      fetchSmsStats();
      fetchSmsFields();
      fetchSmsTypes();
      fetchSmsLogs(1, selectedSmsFilter);
    }
  }, [activeTab]);

  // Refetch SMS logs when filters change
  useEffect(() => {
    if (activeTab === 'sms') {
      fetchSmsLogs(1, selectedSmsFilter);
    }
  }, [smsLogFilters, selectedSmsFilter]);

  const handleSmsCardClick = (filterType) => {
    setSelectedSmsFilter(filterType);
    setSmsLogFilters((prev) => ({ ...prev, status: '' })); // Clear manual status filter
    fetchSmsLogs(1, filterType);
  };

  const closeSmsTemplateModal = () => {
    setShowAddSmsTemplateModal(false);
    setEditingSmsTemplate(null);
    setSmsTemplateForm(emptySmsTemplateForm());
  };

  const handleCreateSmsTemplate = async (e) => {
    e.preventDefault();
    if (!smsTemplateForm.name || !smsTemplateForm.type || !smsTemplateForm.template) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(smsTemplateForm),
      });
      if (response.ok) {
        closeSmsTemplateModal();
        fetchSmsTemplates();
        fetchSmsStats();
        alert('SMS template created successfully');
      } else {
        const error = await response.json();
        alert(`Failed to create SMS template: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating SMS template:', error);
      alert('Error creating SMS template');
    }
  };

  const handleUpdateSmsTemplate = async (templateId, formData) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setEditingSmsTemplate(null);
        fetchSmsTemplates();
        fetchSmsStats();
        alert('SMS template updated successfully');
      } else {
        const error = await response.json();
        alert(`Failed to update SMS template: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating SMS template:', error);
      alert('Error updating SMS template');
    }
  };

  const handleDeleteSmsTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this SMS template?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        fetchSmsTemplates();
        fetchSmsStats();
        alert('SMS template deleted successfully');
      } else {
        const error = await response.json();
        alert(`Failed to delete SMS template: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting SMS template:', error);
      alert('Error deleting SMS template');
    }
  };

  const handleToggleSmsTemplateStatus = async (templateId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      if (response.ok) {
        fetchSmsTemplates();
        fetchSmsStats();
        alert(`SMS template ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      } else {
        const error = await response.json();
        alert(`Failed to update SMS template: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating SMS template status:', error);
      alert('Error updating SMS template status');
    }
  };

  const handlePreviewSmsTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sms-management/templates/${templateId}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setSmsPreview(data.preview);
        setShowSmsPreview(true);
      } else {
        alert('Failed to preview SMS template');
      }
    } catch (error) {
      console.error('Error previewing SMS template:', error);
      alert('Error previewing SMS template');
    }
  };

  const insertSmsField = (field) => {
    setSmsTemplateForm((prev) => ({ ...prev, template: prev.template + `{{${field}}}` }));
  };

  const handleEditSmsTemplate = (template) => {
    setSmsTemplateForm({
      name: template.name || '',
      type: template.type || '',
      template: template.template || '',
      description: template.description || '',
      category: template.category || '',
      maxLength: template.maxLength || 160,
      priority: template.priority || 1,
      sendTime: template.sendTime || '',
      frequency: template.frequency || 'immediate',
      isDefault: template.isDefault || false,
      language: template.language || 'en',
      isActive: template.isActive !== undefined ? template.isActive : true,
    });
    setEditingSmsTemplate(template);
    setShowAddSmsTemplateModal(true);
  };

  const handleSmsTemplateSubmit = async (e) => {
    e.preventDefault();
    if (!smsTemplateForm.name || !smsTemplateForm.type || !smsTemplateForm.template) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      if (editingSmsTemplate) {
        await handleUpdateSmsTemplate(editingSmsTemplate.id, smsTemplateForm);
      } else {
        await handleCreateSmsTemplate(e);
      }
    } catch (error) {
      console.error('Error submitting SMS template:', error);
    }
  };

  const closeSmsLogDetails = () => {
    setShowSmsLogDetails(false);
    setSelectedSmsLog(null);
  };

  const viewSmsLog = (log) => {
    setSelectedSmsLog(log);
    setShowSmsLogDetails(true);
  };

  return {
    smsTemplates, loadingSmsTemplates, smsLogs, loadingSmsLogs, smsStats, smsFields, smsTypes, smsCategories,
    showAddSmsTemplateModal, setShowAddSmsTemplateModal, editingSmsTemplate, closeSmsTemplateModal,
    selectedSmsFilter, setSelectedSmsFilter, smsLogPagination,
    selectedSmsLog, showSmsLogDetails, closeSmsLogDetails, viewSmsLog,
    smsLogFilters, setSmsLogFilters, smsTemplateForm, setSmsTemplateForm,
    smsPreview, showSmsPreview,
    fetchSmsLogs, handleSmsCardClick,
    handleDeleteSmsTemplate, handleToggleSmsTemplateStatus, handlePreviewSmsTemplate,
    insertSmsField, handleEditSmsTemplate, handleSmsTemplateSubmit,
  };
}
